import { useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { App } from 'antd'
import type { StakeholderTemplate, ProjectStakeholder } from '@bandos/shared'
import type { RightPanelView } from '../CharacterSelectionModal.types'
import { MAX_TEAM_SIZE } from '../CharacterSelectionModal.types'

interface UseTeamOperationsParams {
  mindmapId: string | undefined
  projectStakeholders: ProjectStakeholder[]
  templates: StakeholderTemplate[]
  addStakeholder: (
    mindmapId: string,
    templateId: string
  ) => Promise<{ success: boolean; error?: string; errorCode?: string }>
  removeStakeholder: (
    mindmapId: string,
    stakeholderId: string
  ) => Promise<{ success: boolean; error?: string }>
  selectedTemplate: StakeholderTemplate | null
  setSelectedTemplate: (t: StakeholderTemplate | null) => void
  setRightPanelView: (view: RightPanelView) => void
}

export function useTeamOperations({
  mindmapId,
  projectStakeholders,
  templates,
  addStakeholder,
  removeStakeholder,
  selectedTemplate,
  setSelectedTemplate,
  setRightPanelView,
}: UseTeamOperationsParams) {
  const { message } = App.useApp()

  const [operatingIds, setOperatingIds] = useState<Set<string>>(new Set())

  const effectiveLimit = MAX_TEAM_SIZE
  const isTeamFull = projectStakeholders.length >= MAX_TEAM_SIZE

  const isTemplateInTeam = useCallback(
    (templateId: string): boolean => {
      return projectStakeholders.some((ps) => ps.stakeholder_template_id === templateId)
    },
    [projectStakeholders]
  )

  const getTemplateForSlot = useCallback(
    (index: number): StakeholderTemplate | null => {
      const stakeholder = projectStakeholders[index] as ProjectStakeholder | undefined
      if (!stakeholder) return null
      return templates.find((t) => t.id === stakeholder.stakeholder_template_id) ?? null
    },
    [projectStakeholders, templates]
  )

  const handleAddToTeam = useCallback(
    async (template: StakeholderTemplate, e: React.MouseEvent) => {
      e.stopPropagation()
      if (!mindmapId || isTemplateInTeam(template.id)) return
      if (isTeamFull) return

      setOperatingIds((prev) => new Set(prev).add(template.id))
      try {
        const result = await addStakeholder(mindmapId, template.id)
        if (result.success) {
          message.success(`${template.name} added to team`)
        } else {
          if (result.errorCode === 'STAKEHOLDER_LIMIT_REACHED') {
            message.error('Team is full')
          } else {
            message.error(result.error ?? 'Failed to add')
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'mindmap.stakeholder', action: 'add_to_team' },
        })
        message.error('Failed to add team member')
      } finally {
        setOperatingIds((prev) => {
          const next = new Set(prev)
          next.delete(template.id)
          return next
        })
      }
    },
    [mindmapId, isTemplateInTeam, isTeamFull, addStakeholder, message]
  )

  const handleSlotRemove = useCallback(
    async (index: number) => {
      if (!mindmapId) return

      const stakeholder = projectStakeholders[index] as ProjectStakeholder | undefined
      if (!stakeholder) return

      const template = templates.find((t) => t.id === stakeholder.stakeholder_template_id)

      setOperatingIds((prev) => new Set(prev).add(stakeholder.stakeholder_template_id))
      try {
        const result = await removeStakeholder(mindmapId, stakeholder.id)
        if (result.success) {
          message.success(`${template?.name ?? 'Team member'} removed`)
          if (selectedTemplate?.id === stakeholder.stakeholder_template_id) {
            setSelectedTemplate(null)
            setRightPanelView('empty')
          }
        } else {
          message.error(result.error ?? 'Failed to remove')
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'mindmap.stakeholder', action: 'remove_from_team' },
        })
        message.error('Failed to remove team member')
      } finally {
        setOperatingIds((prev) => {
          const next = new Set(prev)
          next.delete(stakeholder.stakeholder_template_id)
          return next
        })
      }
    },
    [
      mindmapId,
      projectStakeholders,
      templates,
      selectedTemplate,
      removeStakeholder,
      message,
      setSelectedTemplate,
      setRightPanelView,
    ]
  )

  return {
    operatingIds,
    effectiveLimit,
    isTeamFull,
    isTemplateInTeam,
    getTemplateForSlot,
    handleAddToTeam,
    handleSlotRemove,
  }
}
